import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import iziToast from "izitoast";
import { useContext, useState } from "react";
import Select from "react-select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "src/components/ui/accordion";
import useMemoAsync from "src/hooks/useMemoAsync";
import { getAllAgentsService } from "src/services/AgentService";
import { Document } from "src/types/Document";
import { capitalize } from "src/utils";
import { DocumentContext } from "./DocumentDetailPageComponent";
import { AppAgentContext } from "src/context/ContextProvider";
import { analyzeDocumentService } from "src/services/DocumentService";
import Icon from "@mdi/react";
import { mdiLoading } from "@mdi/js";
import { useSearchParams } from "react-router-dom";

export const DocumentAnalysisComponent = () => {
  const { state: { session: { user_id }}} = useContext(AppAgentContext);
  const { state: { document } } = useContext(DocumentContext);
  const [values, setValues] = useState({ classification: '', example: ''});
  const [docAnalysis, setDocAnalysis] = useState<Document>(new Document(document));
  const [isLoading, setIsLoading] = useState(false);
  const [focusSelect, setFocusSelect] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const agents = useMemoAsync(async() => {
    const res = await getAllAgentsService();
    if(res.success) {
       return res.items.map(item => ({
        value: item.id, 
        label: capitalize(item.name)
       }));
    }
    return [];
  }, [], []);

  const deleteUrl = (data: string, type: 'classification' | 'example') => {
    const urls = type === 'classification' ? docAnalysis.getContractClassification() : [];
    const deleteUrl = urls.filter(url => url !== data);
    const doc = new Document(docAnalysis);
    switch(type) {
      case "classification":
        doc.setContractClassification(deleteUrl);
        break;
      case "example":
        doc.setContractClassification(deleteUrl);
        break;
    }

    iziToast.success({
      message: type === 'classification' ? 'Clasificación eliminada' : 'Ejemplo eliminado',
      position: 'topCenter'
    });
    setDocAnalysis(doc);
  }

  const analyzeSubmit = async() => {
    setIsLoading(true);
    if(!docAnalysis.agent_id) {
      iziToast.warning({
        message: 'Selecciona un agente',
        position: 'topCenter'
      });
      setIsLoading(false);
      return;
    }
    docAnalysis.setDocId();
    docAnalysis.setUserId(user_id);
    
    const res = await analyzeDocumentService(docAnalysis);
    if(res.success) {
      searchParams.set('mode', 'historial');
      setSearchParams(searchParams);
      setIsLoading(false);
    }
  }

  return (
    <div className="flex gap-5 p-5">
      <div className="w-full">
        <div className="flex justify-end">
          <button className="btn btn-sm flex gap-2" disabled={isLoading} type="button" onClick={analyzeSubmit}>
            Analizar Documento
            {isLoading && <Icon className="mr-1" size={1} path={mdiLoading} spin />}
          </button>
        </div>
      <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
        <AccordionItem value="item-1">
          <AccordionTrigger>Agente</AccordionTrigger>
          <AccordionContent>
            <div className={`grid gap-1 items-center`}
            style={{
              marginBottom: focusSelect ? 160 : 10
            }}
            >
                <label htmlFor="">Seleccionar Agente</label>
                <Select
                  value={docAnalysis.getAgentSelect(agents)}
                  options={agents}
                  onMenuOpen={() => setFocusSelect(true)}
                  onMenuClose={() => setFocusSelect(false)}
                  maxMenuHeight={150}
                  onChange={(option) => {
                    const doc = new Document(docAnalysis);
                    doc.setAgentId(option?.value || 0);
                    setDocAnalysis(doc);
                    // dispatch(AiAgentActions.setAgent({...Agent, vectorStore: option || {value: '', label: ''}}))
                  }}
                  placeholder={'Seleccionar'}
                />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="item-2">
          <AccordionTrigger>Configuración del Texto</AccordionTrigger>
          <AccordionContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-1 items-center mb-5">
                  <label htmlFor="">Tamaño de las partes</label>
                  <input
                      type="number"
                      autoComplete={'off'}
                      value={docAnalysis.getChunkSize()}
                      className="py-2 px-4 w-full rounded border border-gray-400 outline-none"
                      placeholder="Escribe el tamaño"
                      onKeyDown={(event) => {
                        if(event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                      onChange={(event) => {
                        const doc = new Document(docAnalysis);
                        doc.setChunkSize(event.target.value);
                        setDocAnalysis(doc);
                      }}
                  />
              </div>

              <div className="grid gap-1 items-center mb-5">
                <label htmlFor="">Tamaño del overlap</label>
                <input
                    type="number"
                    autoComplete={'off'}
                    value={docAnalysis.getChunkOverlap()}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none"
                    placeholder="Escribe el tamaño"
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                        }
                    }}
                    onChange={(event) => {
                      const doc = new Document(docAnalysis);
                      doc.setChunkOverlap(event.target.value);
                      setDocAnalysis(doc);
                    }}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="item-3">
          <AccordionTrigger>Configuración de Revisión</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-1 items-center mb-5">
              <label htmlFor="">Reglas para el análisis</label>
              <textarea
                  rows={4}
                  autoComplete={'off'}
                  value={docAnalysis.getContractRevision()}
                  className="py-2 px-4 w-full rounded border border-gray-400 outline-none resize-none"
                  placeholder="Escribe tus reglas para el análisis"
                  onChange={(event) => {
                    const doc = new Document(docAnalysis);
                    doc.setContractRevision(event.target.value);
                    setDocAnalysis(doc);
                  }}
                />
            </div>

            <div className="grid gap-1 items-center mb-5">
              <label htmlFor="">Clasificación</label>
              <div className="flex gap-2">
                <input
                    type="text"
                    autoComplete={'off'}
                    value={values.classification}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none resize-none"
                    placeholder="Sin Riesgo, Poco Riesgoso, Riesgoso"
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                        const doc = new Document(docAnalysis);
                        doc.setContractClassification([...doc.getContractClassification(), values.classification]);
                        setDocAnalysis(doc);
                        setValues(prev => ({...prev, classification: ''}));
                        // dispatch(AiAgentActions.setAgent({...Agent, website: '', url_web: [...Agent.url_web, Agent.website]}))
                      }
                    }}
                    onChange={(event) => {
                      setValues(prev => ({...prev, classification: event.target.value}));
                    }}
                />
                <div className="w-20">
                  <button className="flex btn btn-sm h-full items-center" type="button" onClick={() => {
                    const doc = new Document(docAnalysis);
                    doc.setContractClassification([...doc.getContractClassification(), values.classification]);
                    setDocAnalysis(doc);
                    setValues(prev => ({...prev, classification: ''}));
                  }}>
                    + Añadir
                  </button>
                </div>
              </div>
            </div>
            <div className="grid gap-1 items-center mb-5">
              <h2>Clasificaciones Incluidas</h2>
              <div className="flex flex-wrap gap-3 my-2">
                {
                  docAnalysis.getContractClassification().map((classification, index) => (
                    <div key={index} className="p-2 border border-gray-400 rounded flex gap-4">
                      <p className="text-sm">{classification}</p>
                      <span
                          className="cursor-pointer text-red-400 w-5 h-5 flex justify-center items-center"
                          onClick={() => deleteUrl(classification, 'classification')}
                      >
                          <FontAwesomeIcon icon={faTimesCircle as any} />
                      </span>
                    </div>
                  ))
                }
                {
                  docAnalysis.getContractClassification().length === 0 && (
                    <p className="text-gray-300">Sin Clasificación</p>
                  )
                }
              </div>
            </div>

            <div className="grid gap-1 items-center mb-5">
              <label htmlFor="">Ejemplo</label>
              <div className="flex gap-2">
                <input
                    type="text"
                    autoComplete={'off'}
                    value={values.example}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none resize-none"
                    placeholder=""
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                        const doc = new Document(docAnalysis);
                        doc.setContractExample([...doc.getContractExample(), values.example]);
                        setDocAnalysis(doc);
                        setValues(prev => ({...prev, example: ''}));
                        // dispatch(AiAgentActions.setAgent({...Agent, website: '', url_web: [...Agent.url_web, Agent.website]}))
                      }
                    }}
                    onChange={(event) => {
                      setValues(prev => ({...prev, example: event.target.value}));
                    }}
                />
                <div className="w-20">
                  <button className="flex btn btn-sm h-full items-center" type="button" onClick={() => {
                    const doc = new Document(docAnalysis);
                    doc.setContractExample([...doc.getContractExample(), values.example]);
                    setDocAnalysis(doc);
                    setValues(prev => ({...prev, example: ''}));
                  }}>
                    + Añadir
                  </button>
                </div>
              </div>
            </div>
            <div className="grid gap-1 items-center mb-5">
              <h2>Ejemplos Incluidos</h2>
              <div className="flex flex-wrap gap-3 my-2">
                {
                  docAnalysis.getContractExample().map((example, index) => (
                    <div key={index} className="p-2 border border-gray-400 rounded flex gap-4">
                      <p className="text-sm">{example}</p>
                      <span
                          className="cursor-pointer text-red-400 w-5 h-5 flex justify-center items-center"
                          onClick={() => deleteUrl(example, 'example')}
                      >
                          <FontAwesomeIcon icon={faTimesCircle as any} />
                      </span>
                    </div>
                  ))
                }
                {
                  docAnalysis.getContractExample().length === 0 && (
                    <p className="text-gray-300">Sin Ejemplos</p>
                  )
                }
              </div>
            </div>

            <div className="grid gap-1 items-center mb-5">
                <label htmlFor="">Threshold</label>
                <input
                    type="number"
                    autoComplete={'off'}
                    value={docAnalysis.getSentimentThreshold()}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none"
                    placeholder="Escribe el Threshold"
                    min="-Infinity" // Permite números negativos
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                        }
                    }}
                    onChange={(event) => {
                      const doc = new Document(docAnalysis);  
                      doc.setSentimentThreshold(event.target.value);
                      setDocAnalysis(doc);
                    }}
                />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      </div>
    </div>
  )
}
